import React, { useLayoutEffect, useRef, useState } from "react"
import {
  Box, Button, Heading, Link, Text,
} from "@chakra-ui/react"
import Icon from "components/elements/Icon"
import { DIOBOX_UPDATES_LINK } from "utilities/externalLinks"
import ZoomableImage from "./ZoomableImage"
import { Update as Post } from "./types"
import { formatUpdateDay } from "./utils"
import WhatsNewChips from "./WhatsNewChips"
import WhatsNewPostBody from "./WhatsNewPostBody"
import { WHATS_NEW_COLORS, WHATS_NEW_IMAGE_SIZES } from "./colors"

// Folded only well past the limit: a few lines hidden behind a fade read as a bug
const CUT_HEIGHT = 480
const CUT_FROM_HEIGHT = 640

type Props = {
  post: Post
}

const WhatsNewPost = ({ post }: Props) => {
  const body = useRef<HTMLDivElement>(null)
  const [isCut, setIsCut] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const href = `${DIOBOX_UPDATES_LINK}/${post.slug}`

  useLayoutEffect(() => {
    setIsCut((body.current?.scrollHeight ?? 0) > CUT_FROM_HEIGHT)
  }, [])

  const isFolded = isCut && !isExpanded

  return (
    <Box as="article">
      <Text as="time" dateTime={post.publishedAt} display="block" fontSize="15px" color={WHATS_NEW_COLORS.text3}>
        {formatUpdateDay(post.publishedAt)}
      </Text>
      <Heading
        as="h3"
        mt={2}
        fontSize="22px"
        fontWeight="semibold"
        lineHeight={1.3}
        letterSpacing="-0.014em"
        color={WHATS_NEW_COLORS.text1}
        sx={{ textWrap: "balance" }}
      >
        <Link href={href} isExternal transition="opacity 0.15s ease-out" _hover={{ opacity: 0.7 }}>
          {post.title}
        </Link>
      </Heading>
      <WhatsNewChips types={post.types} tags={post.tags} mt={4} />
      {post.cover && (
        <Box mt={6}>
          <ZoomableImage image={post.cover} sizes={WHATS_NEW_IMAGE_SIZES} />
        </Box>
      )}
      {post.body.length > 0 && (
        <Box position="relative" mt={6}>
          <WhatsNewPostBody
            ref={body}
            body={post.body}
            maxH={isFolded ? `${CUT_HEIGHT}px` : undefined}
            overflow={isFolded ? "hidden" : undefined}
          />
          {isFolded && (
            <Box
              position="absolute"
              bottom={0}
              insetX={0}
              h={24}
              bgGradient="linear(to-b, transparent, white)"
              pointerEvents="none"
            />
          )}
        </Box>
      )}
      {isFolded && (
        <Button
          variant="link"
          mt={4}
          color={WHATS_NEW_COLORS.text1}
          fontSize="15px"
          fontWeight="semibold"
          rightIcon={<Icon icon="down-arrow" size={3} />}
          onClick={() => setIsExpanded(true)}
        >
          Continue reading
        </Button>
      )}
    </Box>
  )
}

export default WhatsNewPost
